import React from 'react';
import { Sparkles, Camera, ScanLine, ShieldCheck, ArrowRight, TrendingDown } from 'lucide-react';
import { useReveal } from '../hooks/useInView';
import { useReferralCta } from '../hooks/useReferralCta';

// Sample weekly weight (8-week course, illustrative)
const SERIES = [
  { w: 'W0', kg: 92.4 },
  { w: 'W1', kg: 91.2 },
  { w: 'W2', kg: 89.9 },
  { w: 'W3', kg: 88.7 },
  { w: 'W4', kg: 87.6 },
  { w: 'W5', kg: 86.8 },
  { w: 'W6', kg: 86.1 },
  { w: 'W7', kg: 85.6 }
];

function Spark() {
  // Inline SVG line/area chart of the weight series.
  const W = 600, H = 160, PAD = 20;
  const ks = SERIES.map((p) => p.kg);
  const min = Math.min(...ks) - 0.5;
  const max = Math.max(...ks) + 0.5;
  const xStep = (W - PAD * 2) / (SERIES.length - 1);
  const y = (v) => PAD + ((max - v) / (max - min)) * (H - PAD * 2);
  const path = SERIES.map((p, i) => `${i === 0 ? 'M' : 'L'} ${PAD + i * xStep} ${y(p.kg)}`).join(' ');
  const area = `${path} L ${PAD + (SERIES.length - 1) * xStep} ${H - PAD} L ${PAD} ${H - PAD} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40 sm:h-44">
      <defs>
        <linearGradient id="areaG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#E8833A" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#E8833A" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="lineG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"  stopColor="#0A2A6B" />
          <stop offset="100%" stopColor="#3F5798" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#areaG)" />
      <path d={path} fill="none" stroke="url(#lineG)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {SERIES.map((p, i) => (
        <g key={p.w}>
          <circle cx={PAD + i * xStep} cy={y(p.kg)} r={i === SERIES.length - 1 ? 5 : 3} fill="#1A1F2E" />
          {i === SERIES.length - 1 && (
            <text x={PAD + i * xStep - 6} y={y(p.kg) - 10} fontSize="11" fill="#1A1F2E" fontWeight="600">
              {p.kg} kg
            </text>
          )}
        </g>
      ))}
      {SERIES.map((p, i) => (
        <text key={'lbl-' + p.w} x={PAD + i * xStep} y={H - 4} fontSize="10" fill="#74747A" textAnchor="middle">{p.w}</text>
      ))}
    </svg>
  );
}

export default function ReportPreview() {
  const left = useReveal();
  const right = useReveal({ threshold: 0.05 });
  const { ctaUrl } = useReferralCta();

  return (
    <section className="section bg-ink-50">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div ref={left.ref} className={`${left.className} lg:col-span-5`}>
            <span className="eyebrow">Your dashboard</span>
            <h2 className="h-section mt-4">Track every kilo, every week.</h2>
            <p className="lead mt-4">
              Your private dashboard turns weekly weigh-ins, progress photos, and lab
              follow-ups into a clear story — with AI-summarised insights you can show
              your doctor.
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                  <ScanLine size={16} />
                </div>
                <div>
                  <div className="font-semibold text-ink-900">Weekly weigh-ins</div>
                  <div className="text-sm text-ink-500">Logged at the clinic or by you at home.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                  <Camera size={16} />
                </div>
                <div>
                  <div className="font-semibold text-ink-900">Before / after photos</div>
                  <div className="text-sm text-ink-500">Private to you. Shareable on demand.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                  <Sparkles size={16} />
                </div>
                <div>
                  <div className="font-semibold text-ink-900">AI-summarised progress</div>
                  <div className="text-sm text-ink-500">Plain-English read on your trajectory.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                  <ShieldCheck size={16} />
                </div>
                <div>
                  <div className="font-semibold text-ink-900">Yours forever</div>
                  <div className="text-sm text-ink-500">PDPA-aligned storage. Export or delete anytime.</div>
                </div>
              </li>
            </ul>

            <div className="mt-8">
              <a href={ctaUrl} className="btn-brand">
                Am I Qualified? <ArrowRight size={16} />
              </a>
            </div>
          </div>

          <div ref={right.ref} className={`${right.className} lg:col-span-7`}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-brand-100/60 via-white to-icy/70 blur-2xl rounded-[40px]" />
              <div className="relative card-elevated p-6 sm:p-8">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-ink-500">8-week course · Bottle 2 of 2</div>
                    <h3 className="text-xl font-semibold text-ink-900 mt-1">Aisha · Standard plan</h3>
                    <div className="text-xs text-ink-500 mt-1">Clinic: KL Weight Clinic · Last visit 14 Mar 2026</div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs text-ink-500">Total loss</div>
                      <div className="text-2xl font-semibold text-emerald-700 flex items-center gap-1 justify-end">
                        <TrendingDown size={18} /> 6.8 kg
                      </div>
                    </div>
                    <div className="w-14 h-14 rounded-full border-[5px] border-emerald-500/80 flex items-center justify-center text-emerald-700 font-semibold text-sm">
                      −7%
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-xl bg-gradient-to-r from-brand-50 to-icy border border-brand-200 px-4 py-4">
                  <div className="flex items-start gap-3">
                    <Sparkles size={18} className="text-brand-700 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-ink-900">AI insight</div>
                      <p className="text-sm text-ink-700 mt-1">
                        On track for your goal. Loss has slowed from 1.2 kg/wk to 0.5 kg/wk —
                        normal at this stage. Stay hydrated and continue weekly visits.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-xl bg-white border border-ink-100 p-3 sm:p-4">
                  <Spark />
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-xl bg-ink-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">Start</div>
                    <div className="mt-1 font-semibold text-ink-900">92.4 kg</div>
                  </div>
                  <div className="rounded-xl bg-ink-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">Now</div>
                    <div className="mt-1 font-semibold text-emerald-700">85.6 kg</div>
                  </div>
                  <div className="rounded-xl bg-ink-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">Target</div>
                    <div className="mt-1 font-semibold text-ink-900">82.0 kg</div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-ink-100">
                  <div className="text-xs text-ink-500">
                    Updates each week after your clinic visit
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-outline btn !py-2 !px-4 text-xs">Photos</button>
                    <button className="btn-primary !py-2 !px-4 text-xs">Share with GP</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
